import { createFileRoute } from '@tanstack/react-router'
import { useCallback } from 'react'
import * as L from 'partial.lenses'

import { Event, WithoutMetadata } from '@/types'

import { cleanMetadataValues } from '@/backend'
import { getEventRemoveBlockers, useDeleteEvent, usePatchEvent } from '@/services/events'

import { DateField, DateRangeField, formFor, patchStrategy, useAutosavingState } from '@/libraries/forms'
import { PageSection } from '@/libraries/ui'
import { BallroomSelect } from '@/components/ballroom/BallroomSelect'
import { JSONPatch } from '@/components/event/EventProgramForm/patchStrategy'
import { EventRegistrationSystemSelector } from '@/components/event/EventRegistrationSystemSelector'
import { EventGrantsEditor } from '@/components/rights/EventGrantsEditor'
import { DeleteButton } from '@/components/widgets/DeleteButton'
import { useT } from '@/i18n'

import { useCurrentEvent } from './-context'

export const Route = createFileRoute(
  '/events/$eventId/{-$eventVersionId}/edit',
)({
  component: RouteComponent,
  staticData: {
    breadcrumb: 'routes.events.event.edit.title',
    requireRights: ({ eventId }) => ({
      rights: 'events:read',
      entityId: eventId,
    }),
  },
})

const {
  Input,
  Form,
  Field,
} = formFor<WithoutMetadata<Event>>()

function RouteComponent() {
  const event = useCurrentEvent()
  const t = useT('routes.events.event.edit')
  const label = useT('domain.event')
  const navigate = Route.useNavigate()
  const [deleteEvent] = useDeleteEvent({
    refetchQueries: ['getEvents'],
    onCompleted: () => navigate({ to: '/' }),
  })

  const [patchEvent] = usePatchEvent()
  const patch = useCallback(
    (eventPatch: JSONPatch) => patchEvent({ id: event._id, event: eventPatch }),
    [event._id, patchEvent],
  )
  const { state, formProps } = useAutosavingState<WithoutMetadata<Event>, JSONPatch>(
    cleanMetadataValues<Event>(event),
    patch,
    patchStrategy.jsonPatchWithFields([
      'name',
      'beginDate',
      'endDate',
      'program',
      'eventRegistrationSystem',
      'accessControl',
    ], L.modify(['program'],
      ({ ballroom, ...rest }) => ({
        ...rest,
        ballroomId: ballroom?._id ?? null,
      })),
    ),
  )
  const registrationSystemReadOnly = event.eventRegistrationSystem === 'Kompassi' && event._hasRegisteredVolunteers
  const deleteBlockers = getEventRemoveBlockers(event)

  return <PageSection title={t('title')} syncStatus={state} toolbar={
    <DeleteButton
      minimal
      requireRight="events:delete"
      entityId={event._id}
      disabled={deleteBlockers.length > 0}
      tooltip={
        deleteBlockers.map(blocker => label(`EventRemoveBlocker.${blocker}`)).join('\n') || undefined
      }
      onDelete={() => deleteEvent({ id: event._id })}
      text={t('deleteEvent')}
      confirmText={t('eventDeleteConfirmation', { eventName: event.name })}
    />
  }>
    <Form {...formProps}>
      <div className="flex flex-wrap gap-6">
        <Input label={label('name')} path="name" required containerClassName="w-100" />
        <DateRangeField<Event>
          id="eventDate"
          label={label('eventDate')}
          beginPath="beginDate"
          endPath="endDate"
          required
        />
      </div>
      <div className="flex flex-wrap gap-6">
        <DateField<Event>
          required
          label={(label('ballDateTime'))}
          path="program.dateTime"
          showTime
          minDate={formProps.value.beginDate}
          maxDate={formProps.value.endDate}
        />
        <Field
          label={label('ballroom')}
          path="program.ballroom"
          component={BallroomSelect}
        />
      </div>
      <Field
        readOnly={registrationSystemReadOnly}
        label={label('eventRegistrationSystem')}
        path="eventRegistrationSystem"
        component={EventRegistrationSystemSelector}
        helperText={
          registrationSystemReadOnly
            ? t('cannotChangeEventRegistrationSystem')
            : undefined
        }
      />
      <EventGrantsEditor eventId={event._id} />
    </Form>
  </PageSection>
}
